<template>
  <div class="pwa-install-overlay" v-if="visible" @click.self="handleOverlayClick">
    <div class="pwa-install-modal">
      <div class="modal-icon">
        <img :src="iconPath" alt="Sarvban" />
      </div>
      <h3 class="modal-title">{{ $t('pwaModal.title') }}</h3>
      <p class="modal-desc">{{ $t('pwaModal.description') }}</p>

      <div class="feature-list">
        <div class="feature-item">
          <i class="fa-solid fa-bolt"></i>
          <span>{{ $t('pwaModal.feature_1') }}</span>
        </div>
        <div class="feature-item">
          <i class="fa-solid fa-bell"></i>
          <span>{{ $t('pwaModal.feature_2') }}</span>
        </div>
        <div class="feature-item">
          <i class="fa-solid fa-wifi"></i>
          <span>{{ $t('pwaModal.feature_3') }}</span>
        </div>
      </div>

      <div class="modal-actions">
        <button class="btn-install" @click="handleInstall" :disabled="installing">
          <span v-if="!installing">{{ $t('pwaModal.install') }}</span>
          <span v-else>{{ $t('pwaModal.installing') }}</span>
        </button>
        <button class="btn-notify" @click="handleNotify" :disabled="notifying">
          <span v-if="!notifying">{{ $t('pwaModal.notify') }}</span>
          <span v-else>{{ $t('pwaModal.notifying') }}</span>
        </button>
        <button class="btn-close" @click="close">{{ $t('pwaModal.later') }}</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { usePushNotifications } from '~/composables/usePushNotifications'
import { useAuth } from '~/composables/useAuth'
import { runNotificationPermissionFlow } from '~/utils/notification-permission'

const emit = defineEmits(['installed', 'notified', 'closed'])

const visible = ref(false)
const installing = ref(false)
const notifying = ref(false)
const deferredPrompt = ref(null)
const isIOS = ref(false)
const iconPath = '/market/img/sv-192.png'

const { user } = useAuth()
const vapidKey = useRuntimeConfig().public?.vapidPublicKey

const detectIOS = () => {
  if (typeof window === 'undefined') return false
  const ua = window.navigator.userAgent.toLowerCase()
  const iOS = /iphone|ipad|ipod/.test(ua)
  const standalone = window.matchMedia('(display-mode: standalone)').matches ||
                     window.navigator.standalone === true
  return iOS && !standalone
}

const isDismissed = () => {
  return localStorage.getItem('pwa-install-dismissed') === 'true'
}

const markDismissed = () => {
  localStorage.setItem('pwa-install-dismissed', 'true')
}

let handleBeforeInstallPrompt = null
let modalTimeout = null
let fallbackTimeout = null

const open = () => {
  if (isDismissed()) return
  clearPromptTimers()
  visible.value = true
}

const clearPromptTimers = () => {
  if (modalTimeout) clearTimeout(modalTimeout)
  if (fallbackTimeout) clearTimeout(fallbackTimeout)
}

const close = () => {
  visible.value = false
  clearPromptTimers()
  markDismissed()
  emit('closed')
}

const handleOverlayClick = () => {
  close()
}

const handleInstall = async () => {
  installing.value = true
  try {
    if (deferredPrompt.value) {
      deferredPrompt.value.prompt()
      const { outcome } = await deferredPrompt.value.userChoice
      if (outcome === 'accepted') {
        emit('installed', true)
      }
      deferredPrompt.value = null
    } else if (isIOS.value) {
      // iOS: show guide via parent or scroll to top
      emit('installed', false)
    }
  } catch {
    // ignore
  } finally {
    installing.value = false
    close()
  }
}

const handleNotify = async () => {
  notifying.value = true

  const permission = await runNotificationPermissionFlow({
    notificationApi: typeof Notification === 'undefined' ? null : Notification,
    onResult: (result) => emit('notified', result),
    onComplete: () => {
      notifying.value = false
      close()
    },
  })

  if (permission !== 'granted') return

  // Keep the modal closed even if service-worker registration or push
  // subscription is slow or unavailable.
  usePushNotifications({
    applicationServerKey: vapidKey,
    uid: user.value?.id || user.value?.uid,
  }).subscribe().catch((e) => {
    console.error('Push subscribe failed:', e)
  })
}

onMounted(() => {
  isIOS.value = detectIOS()

  handleBeforeInstallPrompt = (e) => {
    e.preventDefault()
    deferredPrompt.value = e
    console.log('PWA: beforeinstallprompt event received')
  }

  window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)

  // Show modal after a short delay, or after 10s if beforeinstallprompt never fires
  modalTimeout = setTimeout(() => {
    modalTimeout = null
    open()
  }, 4000)

  // Fallback: if beforeinstallprompt doesn't fire within 15s, still show the modal
  fallbackTimeout = setTimeout(() => {
    fallbackTimeout = null
    if (!deferredPrompt.value) {
      console.log('PWA: beforeinstallprompt not detected, showing manual install prompt')
      open()
    }
  }, 15000)
})

onUnmounted(() => {
  if (handleBeforeInstallPrompt) {
    window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
  }
  clearPromptTimers()
})

defineExpose({ open, close, isIOS })
</script>

<style lang="scss" scoped>
.pwa-install-overlay {
  position: fixed;
  inset: 0;
  z-index: 1100;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.pwa-install-modal {
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: $r-lg;
  padding: 24px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
  text-align: center;
  animation: modalIn 0.35s cubic-bezier(0.16, 1, 0.3, 1);
}

.modal-icon {
  width: 72px;
  height: 72px;
  margin: 0 auto 12px;
  border-radius: $r-lg;
  overflow: hidden;
  box-shadow: $shadow-warm-lg;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.modal-title {
  margin: 0 0 6px;
  font-size: 1.15rem;
  font-weight: 800;
  color: $ink;
}

.modal-desc {
  margin: 0 0 18px;
  font-size: 0.9rem;
  color: $ink-soft;
  line-height: 1.6;
}

.feature-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
  text-align: right;

  .feature-item {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 0.88rem;
    color: $ink;

    i {
      width: 28px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      background: rgba($leaf, 0.1);
      color: $leaf;
      font-size: 0.8rem;
    }
  }
}

.modal-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;

  button {
    width: 100%;
    padding: 12px;
    border-radius: $r-pill;
    font-size: 0.9rem;
    font-weight: 700;
    font-family: inherit;
    cursor: pointer;
    transition: all 0.25s;
    border: none;
  }

  .btn-install {
    background: $leaf;
    color: #fff;

    &:hover {
      background: $leaf-deep;
    }

    &:active {
      transform: scale(0.97);
    }

    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }
  }

  .btn-notify {
    background: #fff;
    color: $leaf;
    border: 1.5px solid $leaf;

    &:hover {
      background: rgba($leaf, 0.06);
    }

    &:active {
      transform: scale(0.97);
    }

    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }
  }

  .btn-close {
    background: transparent;
    color: $ink-soft;

    &:hover {
      color: $ink;
    }
  }
}

@keyframes modalIn {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
</style>
